<template>
  <v-dialog
    v-model="model"
    :persistent="busy"
    max-width="650"
    transition="dialog-bottom-transition"
    @keydown.esc="close"
  >
    <v-card class="snm modal-card">
      <v-btn icon class="close-btn" @click.stop="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <h2 class="modal-title">Выделить номера</h2>

      <div v-if="isAdmin" class="form-control">
        <div class="field-label">Пользователь</div>
        <v-autocomplete
          v-if="!isUserLocked"
          ref="userAuto"
          v-model="selectedUser"
          :items="userOptions"
          :loading="loadingUsers"
          item-text="name"
          item-value="id"
          dense
          outlined
          hide-details
          class="field"
          placeholder="Выберите пользователя"
          attach
          :menu-props="{ offsetY: true, maxHeight: 320 }"
          clearable
          cache-items
        />
        <v-text-field
          v-else
          :value="userLabel"
          outlined
          dense
          hide-details
          class="field"
          readonly
        />
      </div>

      <v-radio-group
        v-if="!selectedSegment"
        v-model="type"
        class="radio-group"
        mandatory
        hide-details
      >
        <v-radio
          class="radio"
          color="#4B5EFF"
          value="new"
          label="Создать новую карусель"
        />
        <v-radio class="radio" color="#4B5EFF" value="replace">
          <template #label>
            Заменить номера в карусели
            <v-tooltip bottom max-width="420">
              <template #activator="{ on, attrs }">
                <v-icon small class="help-icon" v-bind="attrs" v-on="on"
                  >mdi-help-circle</v-icon
                >
              </template>
              <span
                >Старая карусель станет «Только входящие», новая получит свежие
                номера.</span
              >
            </v-tooltip>
          </template>
        </v-radio>
      </v-radio-group>

      <div v-if="type === 'new'" class="form-control">
        <div class="field-label">Введите название</div>
        <v-text-field
          v-model="title"
          dense
          outlined
          hide-details
          :placeholder="defaultTitle"
          class="field"
        />
      </div>
      <div v-else class="form-control">
        <div class="field-label">Выберите карусель</div>
        <v-select
          v-model="selectedCarousel"
          :disabled="!!selectedSegment"
          :items="carousels"
          item-text="name"
          item-value="id"
          dense
          outlined
          hide-details
          placeholder="Карусель…"
          class="field"
          attach
          :menu-props="{ offsetY: true, maxHeight: 320 }"
        />
      </div>

      <div v-if="type === 'new'" class="form-control">
        <div class="field-label">Скрипт приема входящих звонков</div>
        <v-autocomplete
          v-model="scriptId"
          :items="scriptOptions"
          :search-input.sync="scriptQuery"
          :filter="filterScripts"
          item-text="name"
          item-value="id"
          dense
          outlined
          hide-details
          class="field"
          placeholder="Начните вводить имя скрипта…"
          :loading="loadingScripts"
          :disabled="loadingScripts"
          attach
          :menu-props="{ offsetY: true, maxHeight: 320 }"
          no-data-text="Скриптов не найдено"
          @focus="ensureScriptsLoaded"
          :rules="[(v) => !!v || 'Обязательное поле']"
          required
        >
          <template #item="{ item }">
            <div
              style="display: flex; flex-direction: column; line-height: 1.2"
            >
              <span style="font-weight: 600">{{ item.name }}</span>
              <span style="font-size: 12px; opacity: 0.75"
                >id: {{ item.id }}</span
              >
            </div>
          </template>
        </v-autocomplete>
      </div>

      <CarouselAutoSettings
        v-if="type === 'new'"
        v-model="autoForm"
        :reserve-options="reserveOptions"
        :loading-reserves="loadingReserves"
        @load-reserves="loadActiveReserves"
        @change="autoSettingsPayload = $event"
      />

      <v-radio-group
        v-if="!selectedSegment"
        v-model="numbersKind"
        class="radio-group"
        mandatory
        hide-details
      >
        <v-radio
          class="radio"
          color="#4B5EFF"
          value="regular"
          label="Обычные номера"
        />
        <v-radio
          class="radio"
          color="#4B5EFF"
          value="reserve"
          label="Номера с маркировкой (из резерва)"
        />
      </v-radio-group>

      <div v-if="numbersKind === 'reserve'" class="form-control">
        <div class="field-label">Выберите резерв (статус «Активен»)</div>
        <v-select
          v-model="selectedReserve"
          :items="reserveOptions"
          item-text="label"
          item-value="id"
          dense
          outlined
          hide-details
          class="field"
          placeholder="Резерв…"
          :loading="loadingReserves"
          :disabled="loadingReserves"
          attach
          :menu-props="{ offsetY: true, maxHeight: 320 }"
          @focus="loadActiveReserves"
        >
          <template #item="{ item }">
            <div
              class="reserve-item"
              :class="{ 'is-empty': (item.available || 0) <= 0 }"
            >
              <div class="reserve-title">«{{ item.name }}»</div>
              <div class="reserve-meta">
                <span v-if="item.marking" class="reserve-mark"
                  >Маркировка: {{ item.marking }}</span
                >
                <span class="reserve-available"
                  >доступно: {{ item.available }}</span
                >
              </div>
            </div>
          </template>

          <template #selection="{ item }">
            <div class="reserve-selection">
              <span>«{{ item.name }}»</span>
              <span v-if="item.marking" class="reserve-selection-mark">
                · {{ item.marking }}</span
              >
            </div>
          </template>
        </v-select>
      </div>

      <div class="form-control">
        <div class="field-label">
          Количество номеров
          <span
            v-if="numbersKind === 'reserve' && selectedReserve"
            class="muted"
          >
            (доступно: {{ reserveLeft }})
          </span>
        </div>
        <v-text-field
          v-model.number="amount"
          :readonly="!!selectedSegment"
          type="number"
          :min="1"
          :step="requiredMultiple"
          :loading="loadingCountRule"
          :max="numbersKind === 'reserve' ? Math.max(0, reserveLeft) : null"
          dense
          outlined
          hide-details
          class="field"
          @input="onAmountInput"
          @keydown.native="preventOverMax"
          @paste.native.prevent="onPasteAmount"
        />
        <div
          v-if="numbersKind !== 'reserve' && !selectedSegment && countRuleReady"
          class="muted mt-1"
        >
          Количество должно быть кратно {{ requiredMultiple }}
        </div>
      </div>
      <div
        class="price-card"
        :class="{ 'reserve-mode': numbersKind === 'reserve' }"
      >
        <div
          v-if="loadingPrice && numbersKind !== 'reserve'"
          class="price-loader-wrap"
        >
          <span class="loader"></span>
        </div>
        <template v-else>
          <div class="price-col price-col-center">
            <div class="price-label">
              {{
                numbersKind === 'reserve' ? 'Оплата' : 'Со счёта будет списано:'
              }}
            </div>
            <div class="price-value">
              {{ (numbersKind === 'reserve' ? 0 : total) | rub }}
              <span v-if="numbersKind === 'reserve'" class="muted">
                — из резерва</span
              >
            </div>
          </div>
        </template>
      </div>

      <div v-if="apiError" class="money-error">{{ apiError }}</div>

      <v-btn
        block
        height="48"
        class="primary-btn mt-4"
        :disabled="
          disabled ||
          busy ||
          loadingPrice ||
          loadingCountRule ||
          (numbersKind !== 'reserve' && !selectedSegment && !countRuleReady)
        "
        :loading="busy"
        @click="confirm"
      >
        Выделить
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
import CarouselAutoSettings from './CarouselAutoSettings.vue';
export default {
  name: 'SelectNumbersModal',

  components: {
    CarouselAutoSettings,
  },

  props: {
    value: { type: Boolean, required: true },
    mode: { type: String, default: 'new' },
    carousels: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    isAdmin: { type: Boolean, default: false },
    createUrl: { type: String, default: '/numbers/create_carousel' },
    allUsersUrl: { type: String, default: '/numbers/users_all' },
    reservesListUrl: { type: String, default: '/numbers/reserves/list' },
  },

  data() {
    return {
      type: this.mode,
      title: '',
      selectedCarousel: null,
      selectedSegment: null,
      selectedTrunk: null,

      selectedUser: null,
      loadingUsers: false,
      allUsers: [],

      amount: 10,
      requiredMultiple: 1,
      loadingCountRule: false,
      countRuleRequestId: 0,
      countRuleReady: false,
      trunkPrices: [],
      segmentPrice: null,
      loadingPrice: false,

      numbersKind: 'regular',
      selectedReserve: null,
      reserveOptions: [],
      loadingReserves: false,
      reserveLeft: 0,

      busy: false,
      apiError: '',

      scriptId: null,
      autoForm: {},
      autoSettingsPayload: {},
      scriptOptions: [],
      loadingScripts: false,
      scriptQuery: '',
      scriptsLoadedFor: '',
    };
  },

  computed: {
    model: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },

    isUserLocked() {
      return this.type === 'replace';
    },

    userOptions() {
      const base = (this.users || [])
        .map((u) =>
          u && typeof u === 'object'
            ? { id: u.id || u.name || '', name: u.name || u.id || '' }
            : { id: String(u || ''), name: String(u || '') },
        )
        .filter((u) => u.id && u.id !== '__all__');
      return this.allUsers.length ? this.allUsers : base;
    },

    userLabel() {
      const id = String(this.selectedUser || '');
      const src = this.allUsers.length ? this.allUsers : this.users || [];
      const f = src.find((u) => String(u.id || u.name) === id);
      return f ? f.name || f.id : id;
    },

    total() {
      const amount = Math.max(0, Number(this.amount) || 0);
      const prices = Array.isArray(this.trunkPrices) ? this.trunkPrices : [];

      if (!prices.length || amount <= 0) return 0;

      if (this.selectedSegment) {
        const segmentPrice = Number(this.segmentPrice);

        if (!Number.isFinite(segmentPrice)) return 0;

        return Math.ceil(Math.max(0, segmentPrice) * amount);
      }

      const baseCount = Math.floor(amount / prices.length);
      const remainder = amount % prices.length;

      let result = 0;

      prices.forEach((item, index) => {
        const allocatedCount = baseCount + (index < remainder ? 1 : 0);
        const trunkPrice = Math.max(0, Number(item.price) || 0);

        result += trunkPrice * allocatedCount;
      });

      return Math.ceil(result);
    },

    defaultTitle() {
      const d = new Date(),
        p = (n) => String(n).padStart(2, '0');
      return `Карусель от ${p(d.getDate())}.${p(
        d.getMonth() + 1,
      )}.${d.getFullYear()}`;
    },

    disabled() {
      const n = Math.max(0, Number(this.amount) || 0);
      const okUser = this.isAdmin
        ? !!this.selectedUser || this.isUserLocked
        : true;
      const okMode =
        this.type === 'new'
          ? !!(this.title || '').trim()
          : !!this.selectedCarousel;

      const okScript = this.type === 'new' ? !!this.scriptId : true;

      if (this.numbersKind === 'reserve') {
        const hasReserve = !!this.selectedReserve;
        const left = Math.max(0, Number(this.reserveLeft) || 0);
        const okAmount = n > 0 && n <= left && left > 0;
        return !(hasReserve && okAmount && okUser && okMode && okScript);
      } else {
        const multiple = Math.max(1, Number(this.requiredMultiple) || 1);
        const okAmount = n > 0 && this.countRuleReady && n % multiple === 0;

        return !(okAmount && okUser && okMode && okScript);
      }
    },
  },

  watch: {
    selectedCarousel(val) {
      if (this.type !== 'replace') return;
      if (this.selectedSegment) return;

      const item = this.carousels.find((c) => String(c.id) === String(val));
      if (!item?.count) return;

      const max =
        this.numbersKind === 'reserve'
          ? Math.max(1, Math.min(item.count, this.reserveLeft || 0))
          : item.count;
      this.amount = max;
    },
    value(v) {
      if (!v) return;
      this.apiError = '';
      if (this.type === 'new' && !this.title) this.title = this.defaultTitle;
      this.loadPrice();
      if (this.isAdmin && !this.isUserLocked && !this.allUsers.length) {
        this.loadAllUsers();
      }
      if (this.type === 'new') this.ensureScriptsLoaded();
    },

    mode(val) {
      this.type = val || 'new';
    },

    selectedUser() {
      if (this.isAdmin && !this.isUserLocked) {
        this.loadPrice();
        this.reserveOptions = [];
        this.selectedReserve = null;
        this.reserveLeft = 0;
        this.scriptOptions = [];
        this.scriptId = null;
        this.scriptsLoadedFor = '';
        this.countRuleReady = false;

        if (this.selectedUser) {
          this.loadCarouselCountRule();
        }
      }
    },

    numbersKind(newVal) {
      if (newVal === 'reserve') {
        this.reserveOptions = [];
        this.selectedReserve = null;
        this.reserveLeft = 0;
        this.requiredMultiple = 1;
        this.countRuleReady = true;
        return;
      }

      this.countRuleReady = false;
      this.loadCarouselCountRule();
    },

    type(newVal) {
      if (newVal === 'new' && !this.title) this.title = this.defaultTitle;
      if (newVal === 'new') this.ensureScriptsLoaded();
    },

    selectedReserve(newVal) {
      const r = this.reserveOptions.find(
        (x) => String(x.id) === String(newVal),
      );
      this.reserveLeft = r ? Number(r.available || 0) : 0;
      if (
        this.numbersKind === 'reserve' &&
        Number(this.amount) > this.reserveLeft
      ) {
        this.amount = this.reserveLeft || 0;
      }
    },
  },

  methods: {
    close() {
      if (!this.busy) this.model = false;
    },

    filterScripts(item, queryText) {
      const q = (queryText || '').toLowerCase().trim();
      if (!q) return true;
      const name = (item.name || '').toLowerCase();
      const id = (item.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    },

    ensureScriptsLoaded() {
      if (this.type !== 'new') return;
      const u = String(this.selectedUser || '').replace(/\D/g, '');
      const key = this.isAdmin && u.length === 11 ? u : 'self';
      if (this.scriptsLoadedFor === key && this.scriptOptions.length) return;
      this.loadScripts();
    },

    async loadScripts() {
      if (this.loadingScripts || this.type !== 'new') return;
      this.loadingScripts = true;
      try {
        const body = {};
        const u = String(this.selectedUser || '').replace(/\D/g, '');
        if (this.isAdmin && u.length === 11) body.user = u;

        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/scripts/list', body, (r) => resolve(r)),
        );
        const arr = Array.isArray(resp) ? resp : [];
        this.scriptOptions = arr
          .map((s) => ({
            id: s.id || s.script_id || s.key || '',
            name: s.name || s.title || s.id || s.script_id || '',
          }))
          .filter((x) => x.id);

        if (
          this.scriptId &&
          !this.scriptOptions.some(
            (x) => String(x.id) === String(this.scriptId),
          )
        ) {
          this.scriptOptions.unshift({
            id: this.scriptId,
            name: String(this.scriptId),
          });
        }

        this.scriptsLoadedFor = this.isAdmin && u.length === 11 ? u : 'self';
      } finally {
        this.loadingScripts = false;
      }
    },

    async loadAllUsers() {
      this.loadingUsers = true;
      try {
        const resp = await new Promise((resolve) =>
          this.$http.post(this.allUsersUrl, {}, (r) => resolve(r)),
        );
        const items = Array.isArray(resp?.users) ? resp.users : [];
        this.allUsers = items
          .map((u) => ({
            id: u.id || u.name,
            name: u.name || u.id,
          }))
          .filter((u) => u.id);
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadCarouselCountRule() {
      const requestId = ++this.countRuleRequestId;
      if (this.selectedSegment || this.numbersKind === 'reserve') {
        this.requiredMultiple = 1;
        this.countRuleReady = true;
        this.loadingCountRule = false;
        return;
      }

      const user = String(this.selectedUser || '').replace(/\D/g, '');

      if (this.isAdmin && user.length !== 11) {
        this.countRuleReady = false;
        this.loadingCountRule = false;
        return;
      }

      this.loadingCountRule = true;
      this.countRuleReady = false;

      try {
        const body = this.isAdmin ? { user } : {};

        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/carousel_count_rule', body, (r) =>
            resolve(r),
          ),
        );

        if (requestId !== this.countRuleRequestId) return;

        const requiredMultiple = Number(resp?.requiredMultiple);

        if (
          resp === 'error' ||
          !resp?.ok ||
          !Number.isInteger(requiredMultiple) ||
          requiredMultiple <= 0
        ) {
          this.apiError =
            resp?.message || 'Не удалось получить правило количества номеров';
          return;
        }

        this.requiredMultiple = requiredMultiple;
        this.countRuleReady = true;

        const currentAmount = Math.max(1, Number(this.amount) || 1);

        if (currentAmount % requiredMultiple !== 0) {
          this.amount =
            Math.ceil(currentAmount / requiredMultiple) * requiredMultiple;
        }
      } finally {
        if (requestId === this.countRuleRequestId) {
          this.loadingCountRule = false;
        }
      }
    },
    
    async loadPrice() {
      this.loadingPrice = true;
      this.segmentPrice = null;

      try {
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const body = {};

        if (this.isAdmin && !this.isUserLocked) {
          const u = String(this.selectedUser || '').replace(/\D/g, '');
          if (u) body.user = u;
        }

        if (
          this.type === 'replace' &&
          this.selectedCarousel &&
          this.selectedSegment
        ) {
          body.carouselId = String(this.selectedCarousel);
          body.segment = String(this.selectedSegment);
        }

        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/price_per_number', body, (r) => resolve(r)),
        );

        this.trunkPrices = Array.isArray(resp?.prices) ? resp.prices : [];

        if (
          resp?.segmentPrice !== null &&
          resp?.segmentPrice !== undefined &&
          Number.isFinite(Number(resp.segmentPrice))
        ) {
          this.segmentPrice = Number(resp.segmentPrice);
        }

        console.log('[segment price preview]', {
          request: body,
          segmentTrunk: resp?.segmentTrunk,
          segmentPrice: resp?.segmentPrice,
        });
      } finally {
        this.loadingPrice = false;
      }
    },

    async loadActiveReserves() {
      if (this.loadingReserves || this.reserveOptions.length) return;
      this.loadingReserves = true;
      try {
        const body = {
          status: 'active',
          ...(this.isAdmin && !this.isUserLocked && this.selectedUser
            ? { user: String(this.selectedUser).replace(/\D/g, '') }
            : {}),
        };
        const resp = await new Promise((resolve) =>
          this.$http.post(this.reservesListUrl, body, (r) => resolve(r)),
        );
        const items = Array.isArray(resp) ? resp : [];
        this.reserveOptions = items
          .map((x) => {
            const id = x.id;
            const available = Number(x.available || x.availableCount || 0);
            const name =
              x.title || x.reserve || `Резерв #${String(id).slice(0, 8)}`;
            const marking = x.marking || '';
            return {
              id,
              available,
              name,
              marking,
              label: `${name}${
                marking ? ` — ${marking}` : ''
              } — доступно: ${available}`,
            };
          })
          .filter((i) => i.id);
      } finally {
        this.loadingReserves = false;
      }
    },

    onAmountInput() {
      let n = Number(this.amount) || 0;
      if (this.numbersKind === 'reserve') {
        const cap = Math.max(0, Number(this.reserveLeft) || 0);
        if (n > cap) n = cap;
      }
      if (n < 1) n = 1;
      this.amount = n;
    },
    preventOverMax(e) {
      if (this.numbersKind !== 'reserve') return;

      const cap = Math.max(0, Number(this.reserveLeft) || 0);
      const key = e.key;

      const ctrl = e.ctrlKey || e.metaKey;
      const serviceKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
        'Tab',
        'Enter',
      ];
      if (
        serviceKeys.includes(key) ||
        (ctrl && ['a', 'c', 'v', 'x'].includes(key.toLowerCase()))
      )
        return;

      if (!/^\d$/.test(key)) {
        e.preventDefault();
        return;
      }

      const { selectionStart, selectionEnd, value } = e.target;
      const next =
        value.slice(0, selectionStart) + key + value.slice(selectionEnd);
      const n = Number(next.replace(/^0+/, '')) || 0;

      if (n > cap) e.preventDefault();
    },

    onPasteAmount(e) {
      const text =
        (e.clipboardData || window.clipboardData).getData('text') || '';
      const digits = text.replace(/\D/g, '');
      if (!digits) return;

      let n = Number(digits) || 0;
      if (this.numbersKind === 'reserve') {
        const cap = Math.max(0, Number(this.reserveLeft) || 0);
        if (n > cap) n = cap;
      }
      if (n < 1) n = 1;

      this.amount = n;
    },
    ensureReserveOk() {
      if (this.numbersKind === 'reserve' && !this.selectedReserve) {
        this.apiError = 'Выберите активный резерв для выдачи номеров.';
        return false;
      }
      return true;
    },

    confirm() {
      this.apiError = '';
      if (!this.ensureReserveOk()) return;

      const amountNum = Math.max(0, Number(this.amount) || 0);
      const isReserve = this.numbersKind === 'reserve';
      if (!isReserve && !this.selectedSegment) {
        const multiple = Math.max(1, Number(this.requiredMultiple) || 1);

        if (!this.countRuleReady || this.loadingCountRule) {
          this.apiError = 'Не удалось проверить допустимое количество номеров';
          return;
        }

        if (amountNum <= 0 || amountNum % multiple !== 0) {
          this.apiError = `Количество номеров должно быть кратно ${multiple}`;
          return;
        }
      }

      if (isReserve) {
        const left = Math.max(0, Number(this.reserveLeft) || 0);
        if (left <= 0) {
          this.apiError = 'В выбранном резерве нет доступных номеров.';
          return;
        }
        if (amountNum > left) {
          this.apiError = `Доступно только ${left} номер(ов) в этом резерве.`;
          this.amount = left;
          return;
        }
      }

      if (this.type === 'replace') {
        this.$emit('confirm', {
          type: 'replace',
          carouselId: this.selectedCarousel,
          segment: this.selectedSegment,
          amount: amountNum,
          numbersKind: this.numbersKind,
          reserveId: isReserve ? this.selectedReserve : undefined,
          user: this.isAdmin
            ? String(this.selectedUser || '').replace(/\D/g, '')
            : undefined,
        });
        this.close();
        return;
      }

      if (this.busy) return;
      this.busy = true;

      const userParam = this.isAdmin
        ? String(this.selectedUser || '').replace(/\D/g, '')
        : undefined;
      const url = isReserve
        ? '/numbers/create_carousel_from_reserve'
        : this.createUrl;
      const payload = isReserve
        ? {
            title: this.title,
            reserveId: this.selectedReserve,
            count: amountNum,
            user: userParam,
            incoming_script_id: this.scriptId,
            ...this.autoSettingsPayload,
            amount: amountNum,
          }
        : {
            title: this.title,
            count: amountNum,
            user: userParam,
            incoming_script_id: this.scriptId,
            ...this.autoSettingsPayload,
            amount: amountNum,
          };

      this.$http.post(url, payload, (res) => {
        this.busy = false;

        if (res === 'error') {
          this.apiError = 'Ошибка сети или сервера';
          return;
        }
        if (!res || res.ok !== true) {
          this.apiError = res?.message || 'Не удалось создать карусель';
          return;
        }

        this.$emit('created', {
          id: res.id,
          title: payload.title,
          amount: amountNum,
          user: userParam,
          numbersKind: this.numbersKind,
          reserveId: isReserve ? this.selectedReserve : undefined,
        });

        this.close();
      });
    },
  },

  filters: {
    rub(v) {
      const n = Number(v) || 0;
      try {
        return new Intl.NumberFormat('ru-RU').format(n) + ' ₽';
      } catch {
        return n + ' ₽';
      }
    },
  },
};
</script>

<style scoped>
/* палитра и контейнер */
.snm {
  --c-text: #2b2d33;
  --c-muted: #808185;
  --c-line: #d9dade;
  --c-bg: #f8f8f8;
  --c-accent: #4b5eff;
}
.snm.modal-card {
  position: relative;
  padding: 32px 32px 28px;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.14);
}

/* заголовок и крестик */
.snm .modal-title {
  margin: 0 0 20px;
  font: 800 24px/1.2 Manrope, system-ui, sans-serif;
  text-align: center;
  color: var(--c-text);
}
.snm .close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  min-width: 36px;
  height: 36px;
}
.snm .close-btn .v-icon {
  font-size: 20px;
  color: #9c9da1;
}

/* поля и подписи */
.snm .form-control {
  margin-top: 16px;
}
.snm .field-label {
  font: 600 15px/1.2 Manrope, sans-serif;
  color: #404247;
  margin-bottom: 6px;
  text-align: left;
}
.snm .muted {
  color: #808185;
  margin-left: 6px;
}
.snm .field .v-input__slot {
  min-height: 44px;
  border-radius: 10px;
  border-color: var(--c-line) !important;
}
.snm .field .v-input__slot:hover,
.snm .field .v-input--is-focused .v-input__slot {
  border-color: var(--c-accent) !important;
}
.snm .field input,
.snm .field .v-select__selections {
  font: 500 14px/1.2 Manrope, sans-serif;
  color: var(--c-text);
}

/* радиокнопки */
.snm .radio-group {
  margin: 8px 0 0;
}
.snm .radio {
  margin: 2px 0;
}
.snm .help-icon {
  color: #9ea0a6 !important;
  margin-left: 6px;
  vertical-align: middle;
}

/* цена/итоги — карточка */
.snm .price-card {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  background: #fff;
  border: 1px solid var(--c-line);
  border-radius: 12px;
  padding: 14px 16px;
  margin-top: 16px;
}
.snm .price-card.reserve-mode {
  grid-template-columns: 1fr;
} /* прячем левую колонку — одна колонка */
.snm .price-col-center {
  text-align: center;
}
.snm .price-label {
  font: 600 12px/1.2 Manrope, sans-serif;
  color: #7b7d83;
  margin-bottom: 2px;
}
.snm .price-value {
  font: 800 20px/1.25 Manrope, sans-serif;
  color: var(--c-text);
}
.snm .price-value .muted {
  font: 800 16px/1.25 Manrope, sans-serif;
  opacity: 0.7;
}
.snm .price-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}
.snm .price-field .v-input__slot {
  min-height: 40px;
  border-radius: 10px;
  width: 120px;
}
.snm .price-suf {
  font: 800 16px/1.2 Manrope, sans-serif;
  color: var(--c-text);
}

/* ошибка */
.snm .money-error {
  margin-top: 10px;
  font: 600 14px/1.25 Manrope, sans-serif;
  color: #ee505a;
}

/* CTA — фирменный синий */
.primary-btn {
  background: #4b5eff !important;
  color: #fff !important;
  font: 800 16px/1.1 Manrope, sans-serif;
  border-radius: 12px !important;
  text-transform: none;
  letter-spacing: 0;
}
.primary-btn.v-btn--disabled {
  opacity: 0.6 !important;
}

/* Резервы — оформление пунктов и выбранного значения */
.reserve-item {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  padding: 4px 0;
}
.reserve-item.is-empty {
  opacity: 0.6;
}
.reserve-title {
  font-weight: 700;
}
.reserve-meta {
  font-size: 12px;
  color: #7b7d83;
  display: flex;
  gap: 10px;
}
.reserve-selection {
  display: flex;
  align-items: center;
  gap: 6px;
}
.reserve-selection-mark {
  color: #7b7d83;
  font-weight: 600;
}
.loader {
  width: 100%;
  height: 4.8px;
  display: block;
  position: relative;
  overflow: hidden;
}
.loader::after {
  content: '';
  width: 96px;
  height: 4.8px;
  background: #4b5eff;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  animation: hitZak 0.6s ease-in-out infinite alternate;
}

.snm .price-loader-wrap {
  grid-column: 1 / -1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
}

@keyframes hitZak {
  0% {
    left: 0;
    transform: translateX(-1%);
  }
  100% {
    left: 100%;
    transform: translateX(-99%);
  }
}
</style>
